import { useKioskStore } from '@/stores/kiosk';
import { useEffect } from 'react';
import { useAppointmentsStore } from '@/stores/appointments';
import { useUsersStore } from '@/stores/users';
import { useNavigate } from 'react-router-dom';
import { EKioskRouterNames } from '@/types';
import dayjs from 'dayjs';

/**
 * This hook is responsible for controlling the kiosk appointments.
 * It periodically checks for the nearest appointment and verifies
 * if it belongs to the current user. If the nearest appointment
 * is in progress and does not belong to the user, it logs out
 * the user and redirects them to a logout notice page.
 * This ensures that users cannot access the kiosk if they do not have an active appointment.
 */
export const useAppointmentControlInKiosk = () => {
  const kioskAppointments = useKioskStore((state) => state.kioskAppointments);
  const { getAppointmentById, isAllAppointmentsLoaded } = useAppointmentsStore(
    (state) => ({
      getAppointmentById: state.getAppointmentById,
      isAllAppointmentsLoaded: state.isAllAppointmentsLoaded,
    }),
  );
  const { userId, logout } = useUsersStore((state) => ({
    userId: state.userId,
    logout: state.logout,
  }));
  const navigate = useNavigate();

  useEffect(() => {
    const callback = async () => {
      try {
        if (!userId) return;
        if (!isAllAppointmentsLoaded) return;
        if (!kioskAppointments?.length || !kioskAppointments) return;
        const closestAppointment = kioskAppointments[0];
        const timeTillStart = -dayjs().diff(
          dayjs(closestAppointment.start),
          's',
        );
        const isMyAppointment = !!getAppointmentById(closestAppointment.id);
        if (timeTillStart <= 0 && !isMyAppointment) {
          await logout();
          navigate(
            `${EKioskRouterNames.KIOSK}/${EKioskRouterNames.KIOSK__LOGOUT_NOTICE}`,
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    void callback();
  }, [
    getAppointmentById,
    isAllAppointmentsLoaded,
    kioskAppointments,
    logout,
    navigate,
    userId,
  ]);
};
